import { Plus } from "@mynaui/icons-react";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { ITournamentItem } from "../../models/ITournament";
import { useNavigate, useSearchParams } from "react-router-dom";
import FilterDialog, { Filter, FilterButton } from "../../components/FilterDialog";
import { DataTable, DeleteTournamentDialog } from "../../components";
import TournamentCalendar from "../../components/TournamentCalendar";
import { getTournamentCategories, getTournamentList, editTournament, deleteTournament } from "../../api/tournaments";
import { getAllLocations } from "../../api/locations";
import { ILocation } from "../../models/ILocation";
import moment from "moment";
import { ChevronDown, Calendar, Table, X } from "lucide-react";
import { Button, Menu, PageHeader, Segmented, Select, Spinner, Tag, Toolbar } from "../../ui";
import type { TagTone } from "../../ui";
import FastTournamentDialog from "../../components/FastTournamentDialog";
import CreateFromExistingDialog from "../../components/CreateFromExistingDialog";
import "./styles.scss";
import { useDebouncedSearch } from "../../hooks";
import { useAppSelector, useAppDispatch } from "../../store/store";
import { canManageTournaments } from "../../utils/authorization";
import { uploadTournamentCover, base64ToFile, isBase64Image } from "../../services/firebase";
import { setGeneralAlertMessage } from "../../store/slices/generalSlice";

const Tournament = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = useAppSelector((state) => state.auth.currentUser);

  const [tournaments, setTournaments] = useState<ITournamentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInitialLoader, setShowInitialLoader] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [sortColumn, setSortColumn] = useState<"name" | "location" | "startDate" | "status">("status");

  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState<Filter[]>([]);
  const [locations, setLocations] = useState<ILocation[]>([]);
  const [showFastTournamentDialog, setShowFastTournamentDialog] = useState(false);
  const [showCreateFromExistingDialog, setShowCreateFromExistingDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [tournamentToDelete, setTournamentToDelete] = useState<ITournamentItem | null>(null);
  const [viewMode, setViewMode] = useState<"table" | "calendar">("calendar");
  const [calendarMonth, setCalendarMonth] = useState<moment.Moment>(moment().startOf("month"));

  // Ref to track current fetch request and prevent duplicates
  const currentFetchRef = useRef<string>("");
  const isFetchingRef = useRef(false);
  const hasLoadedOnceRef = useRef(false);

  // Track migrated tournament IDs to avoid duplicate migrations
  const migratedIdsRef = useRef<Set<string>>(new Set());
  const [isMigrating, setIsMigrating] = useState(false);

  const filtersApplied = useMemo(() => filters.filter((x) => x.options?.some((o) => o.selected)).length, [filters]);

  const handleFilterApply = (newFilters: Filter[]) => {
    setFilters(newFilters);

    const filtersApplied = newFilters.filter((x) => x.options?.some((o) => o.selected));

    let typeFilters: string[] = [];
    let categoryFilters: string[] = [];
    let statusFilters: string[] = [];
    let locationFilters: string[] = [];
    let publishedFilter = "";

    filtersApplied.forEach((filter) => {
      const value = filter.options ? filter.options.filter((x) => x.selected).map((x) => x.value) : [];

      if (filter.label === "Type") typeFilters = value;
      if (filter.label === "Category") categoryFilters = value;
      if (filter.label === "Status") statusFilters = value;
      if (filter.label === "Location") locationFilters = value;
      if (filter.label === "Published" && value.length > 0) publishedFilter = value[0];
    });

    const newParams = new URLSearchParams(searchParams);

    newParams.delete("type");
    newParams.delete("category");
    newParams.delete("status");
    newParams.delete("location");
    newParams.delete("published");
    newParams.delete("page");

    if (typeFilters.length > 0) newParams.set("type", typeFilters.join(","));
    if (categoryFilters.length > 0) newParams.set("category", categoryFilters.join(","));
    if (statusFilters.length > 0) newParams.set("status", statusFilters.join(","));
    if (locationFilters.length > 0) newParams.set("location", locationFilters.join(","));
    if (publishedFilter) newParams.set("published", publishedFilter);
    setSearchParams(newParams);
  };

  const handleSearch = useCallback(
    (value: string) => {
      setSearchParams((prev) => {
        const prevSearch = prev.get("search") || "";

        // Only update if search value actually changed
        if (prevSearch === value) {
          return prev;
        }

        const newParams = new URLSearchParams(prev);
        if (value) {
          newParams.set("search", value);
        } else {
          newParams.delete("search");
        }
        newParams.delete("page"); // Reset to first page when searching
        return newParams;
      });
    },
    [setSearchParams]
  );

  const { searchValue, setSearchValue } = useDebouncedSearch(handleSearch, 500, searchParams.get("search") || "");

  // Quick filter handlers
  const handleQuickFilterChange = useCallback(
    (filterType: "type" | "status" | "location", value: string) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        if (value && value !== "All") {
          newParams.set(filterType, value);
        } else {
          newParams.delete(filterType);
        }
        newParams.delete("page"); // Reset to first page when filtering
        return newParams;
      });
    },
    [setSearchParams]
  );

  const handleResetFilters = useCallback(() => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      // Keep viewMode, month, year, search, sortColumn, sortDirection
      newParams.delete("type");
      newParams.delete("category");
      newParams.delete("status");
      newParams.delete("location");
      newParams.delete("published");
      newParams.delete("page");
      return newParams;
    });
  }, [setSearchParams]);

  // Get current filter values from URL
  const currentFilters = useMemo(() => {
    const params = Object.fromEntries(searchParams.entries());
    const status = params.status?.split(",") || [];
    const location = params.location?.split(",") || [];
    return {
      status: status.length > 0 ? status[0] : "All",
      location: location.length > 0 ? location[0] : "All",
    };
  }, [searchParams]);

  // Check if any filters are applied
  const hasActiveFilters = useMemo(() => {
    const params = Object.fromEntries(searchParams.entries());
    const type = params.type?.split(",") || [];
    const category = params.category?.split(",") || [];
    const status = params.status?.split(",") || [];
    const location = params.location?.split(",") || [];
    const published = params.published || "";
    return type.length > 0 || category.length > 0 || status.length > 0 || location.length > 0 || published !== "";
  }, [searchParams]);

  const updateSearchParams = useCallback(
    (updater: (params: URLSearchParams) => void) => {
      setSearchParams((prev) => {
        const nextParams = new URLSearchParams(prev);
        updater(nextParams);
        if (nextParams.toString() === prev.toString()) {
          return prev;
        }
        return nextParams;
      });
    },
    [setSearchParams]
  );

  const handleViewModeChange = useCallback(
    (mode: "table" | "calendar") => {
      setViewMode(mode);
      updateSearchParams((params) => {
        params.set("viewMode", mode);
        if (mode === "calendar") {
          params.set("month", (calendarMonth.month() + 1).toString());
          params.set("year", calendarMonth.year().toString());
        } else {
          params.delete("month");
          params.delete("year");
        }
      });
    },
    [calendarMonth, updateSearchParams]
  );

  const handleCalendarMonthChange = useCallback(
    (nextMonth: moment.Moment) => {
      setCalendarMonth(nextMonth);
      if (viewMode !== "calendar") return;
      updateSearchParams((params) => {
        params.set("month", (nextMonth.month() + 1).toString());
        params.set("year", nextMonth.year().toString());
      });
    },
    [updateSearchParams, viewMode]
  );

  const handleViewTournament = (tournament: ITournamentItem) => {
    const params = searchParams.toString();
    navigate(`${tournament.id}${params ? `?${params}` : ""}`);
  };

  const handleDeleteClick = (tournament: ITournamentItem) => {
    setTournamentToDelete(tournament);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!tournamentToDelete) return;

    try {
      const res = await deleteTournament(tournamentToDelete.id);

      if (res.success && res.data) {
        dispatch(setGeneralAlertMessage({
          message: res.message || "Tournament deleted successfully",
          type: "success"
        }));

        // Close dialog and reset state
        setShowDeleteDialog(false);
        setTournamentToDelete(null);

        // Refresh tournament list with same filters applied
        // Reset to previous page if we're on page > 1 and deleting the last item
        const currentPage = searchParams.get("page") ? parseInt(searchParams.get("page")!) : 1;
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);

          // If we're on a page > 1 and deleting the last item, go to previous page
          if (currentPage > 1 && tournaments.length === 1) {
            newParams.set("page", (currentPage - 1).toString());
          }

          // Add timestamp to force refresh while maintaining all filters
          newParams.set("_refresh", Date.now().toString());

          return newParams;
        });
      } else {
        throw new Error(res.message || "Failed to delete tournament");
      }
    } catch (error: any) {
      dispatch(setGeneralAlertMessage({
        message: error?.message || "Failed to delete tournament. Please try again.",
        type: "error"
      }));
      // Re-throw error so dialog can handle it and keep loading state
      throw error;
    }
  };

  const handlePageChange = useCallback(
    (newPage: number) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);

        if (newPage > 1) {
          newParams.set("page", newPage.toString());
        } else {
          newParams.delete("page");
        }

        return newParams;
      });
    },
    [setSearchParams]
  );

  const handleSortChange = useCallback(
    (columnKey: "name" | "location" | "startDate", direction: "asc" | "desc") => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("sortColumn", columnKey);
        newParams.set("sortDirection", direction);
        newParams.delete("page");
        return newParams;
      });
    },
    [setSearchParams]
  );

  const getCategories = async () => {
    try {
      const res = await getTournamentCategories();
      return res.data;
    } catch (error: any) {
      console.error(error.message);
    }
  };

  const parseFiltersFromParams = useCallback(() => {
    const params = Object.fromEntries(searchParams.entries());

    const search = params.search?.split(",")[0];
    const type = params.type?.split(",") || [];
    const category = params.category?.split(",") || [];
    const status = params.status?.split(",") || [];
    const location = params.location?.split(",") || [];
    const published = params.published || "";

    return { type, category, search, status, location, published };
  }, [searchParams]);

  const loadLocations = async () => {
    try {
      const response = await getAllLocations();
      setLocations(response.data || []);
    } catch (error: any) {
      console.error("Error loading locations:", error);
    }
  };

  const getData = useCallback(async () => {
    const categories = await getCategories();
    const { category, type, status, location, published } = parseFiltersFromParams();

    const newFilters: Filter[] = [
      {
        label: "Type",
        options: [
          { label: "Volleyball", value: "Volleyball", selected: type.includes("Volleyball") },
          { label: "Beach Volleyball", value: "Beach Volleyball", selected: type.includes("Beach Volleyball") },
          { label: "Padel", value: "Padel", selected: type.includes("Padel") },
          { label: "Beach Tennis", value: "Beach Tennis", selected: type.includes("Beach Tennis") },
          { label: "Spikeball", value: "Spikeball", selected: type.includes("Spikeball") },
          { label: "Dodgeball", value: "Dodgeball", selected: type.includes("Dodgeball") },
          { label: "Beach Football", value: "Beach Football", selected: type.includes("Beach Football") },
        ],
      },
      {
        label: "Category",
        options: categories
          ? categories.map((item: string) => ({
            label: item,
            value: item,
            selected: category.includes(item),
          }))
          : [],
      },
      {
        label: "Location",
        options: locations.map((loc) => ({
          label: loc.name,
          value: loc.id,
          selected: location.includes(loc.id),
        })),
      },
      {
        label: "Status",
        options: [
          { label: "Active", value: "active", selected: status.includes("active") },
          { label: "Completed", value: "completed", selected: status.includes("completed") },
          { label: "Registration Open", value: "open", selected: status.includes("open") },
          { label: "Registration Closed", value: "closed", selected: status.includes("closed") },
          { label: "Registration Not Open", value: "not-open", selected: status.includes("not-open") },
        ],
      },
      {
        label: "Published",
        radio: true,
        options: [
          { label: "Published", value: "true", selected: published === "true" },
          { label: "Not Published", value: "false", selected: published === "false" },
        ],
      },
    ];

    setFilters(newFilters);
  }, [locations, parseFiltersFromParams]);

  useEffect(() => {
    document.title = "Tournaments";
    loadLocations();
  }, []);

  useEffect(() => {
    if (locations.length > 0) {
      getData();
    }
  }, [locations, getData]);

  useEffect(() => {
    if (locations.length > 0) {
      getData();
    }
  }, [searchParams, locations, getData]);

  useEffect(() => {
    const viewParam = searchParams.get("viewMode");
    const monthParam = searchParams.get("month");
    const yearParam = searchParams.get("year");

    if (viewParam === "table" || viewParam === "calendar") {
      if (viewParam !== viewMode) {
        setViewMode(viewParam);
      }
    }

    if (monthParam && yearParam) {
      const parsedMonth = parseInt(monthParam, 10);
      const parsedYear = parseInt(yearParam, 10);
      if (!Number.isNaN(parsedMonth) && !Number.isNaN(parsedYear)) {
        const nextMonth = moment().year(parsedYear).month(parsedMonth - 1).startOf("month");
        if (!nextMonth.isSame(calendarMonth, "month")) {
          setCalendarMonth(nextMonth);
        }
      }
    }
  }, [searchParams, viewMode, calendarMonth]);


  useEffect(() => {
    const fetchData = async () => {
      // Create a unique key for this fetch request (including view mode and calendar month for calendar view)
      const paramsForKey = new URLSearchParams(searchParams);
      paramsForKey.delete("_refresh");
      if (viewMode === "calendar") {
        paramsForKey.set("viewMode", "calendar");
        paramsForKey.set("month", (calendarMonth.month() + 1).toString()); // moment months are 0-indexed
        paramsForKey.set("year", calendarMonth.year().toString());
      }
      const fetchKey = paramsForKey.toString();

      // If we're already fetching this exact request, skip
      if (isFetchingRef.current && currentFetchRef.current === fetchKey) {
        return;
      }

      // Mark as fetching
      isFetchingRef.current = true;
      currentFetchRef.current = fetchKey;

      // Parse params
      const page = searchParams.get("page");
      const sortCol = searchParams.get("sortColumn");
      const sortDir = searchParams.get("sortDirection");
      const search = searchParams.get("search") || "";
      const type = searchParams.get("type") ? searchParams.get("type")!.split(",") : [];
      const category = searchParams.get("category") ? searchParams.get("category")!.split(",") : [];
      const status = searchParams.get("status") ? searchParams.get("status")!.split(",") : [];
      const location = searchParams.get("location") ? searchParams.get("location")!.split(",") : [];
      const published = searchParams.get("published") || "";

      const pageNum = page ? parseInt(page) : 1;
      const sortColumn =
        sortCol && ["name", "location", "startDate", "status"].includes(sortCol) ? (sortCol as any) : "status";
      const sortDirection = sortDir && ["asc", "desc"].includes(sortDir) ? (sortDir as any) : "asc";

      // Update local state
      setPageNumber(pageNum);
      setSortColumn(sortColumn);
      setSortDirection(sortDirection);
      setLoading(true);

      try {
        // For calendar view, fetch tournaments for the selected month/year
        // moment months are 0-indexed, API expects 1-indexed
        const month = viewMode === "calendar" ? calendarMonth.month() + 1 : undefined;
        const year = viewMode === "calendar" ? calendarMonth.year() : undefined;
        // For calendar view, use a larger page size to get all tournaments for the month
        const effectivePageSize = viewMode === "calendar" ? 1000 : pageSize;

        const res = await getTournamentList(
          pageNum,
          effectivePageSize,
          search,
          sortColumn,
          sortDirection,
          type,
          category,
          status,
          location,
          published,
          month,
          year
        );

        // Only update if this is still the current request
        if (currentFetchRef.current === fetchKey) {
          setTournaments(res.data.items);
          setTotalItems(res.data.pagination.total);
          setTotalPages(res.data.pagination.totalPages);
        }
      } catch (error: any) {
        console.error("Error fetching tournaments:", error.message);
      } finally {
        // Only set loading false if this is still the current request
        if (currentFetchRef.current === fetchKey) {
          setLoading(false);
          isFetchingRef.current = false;
          if (!hasLoadedOnceRef.current) {
            hasLoadedOnceRef.current = true;
            setShowInitialLoader(false);
          }
        }
      }
    };

    fetchData();
  }, [searchParams, pageSize, viewMode, calendarMonth]);

  // Lazy migration: Convert base64 covers to Firebase Storage URLs
  useEffect(() => {
    const migrateBase64Covers = async () => {
      // Find tournaments with base64 covers that haven't been migrated yet
      const tournamentsToMigrate = tournaments.filter(
        (t) => isBase64Image(t.cover) && !migratedIdsRef.current.has(t.id)
      );

      if (tournamentsToMigrate.length === 0) return;

      setIsMigrating(true);
      console.log(`Migrating ${tournamentsToMigrate.length} tournament covers to Firebase Storage...`);

      for (const tournament of tournamentsToMigrate) {
        try {
          // Mark as migrating to prevent duplicate attempts
          migratedIdsRef.current.add(tournament.id);

          // Convert base64 to file
          const file = await base64ToFile(tournament.cover, "tournament-cover.jpg");

          // Upload to Firebase Storage
          const coverURL = await uploadTournamentCover(tournament.id, file);

          // Update the tournament with the new Firebase URL
          await editTournament(tournament.id, { cover: coverURL });

          // Update local state to reflect the change
          setTournaments((prev) => prev.map((t) => (t.id === tournament.id ? { ...t, cover: coverURL } : t)));

          console.log(`✓ Migrated tournament "${tournament.name}" cover to Firebase Storage`);
        } catch (error: any) {
          console.error(`Failed to migrate tournament "${tournament.name}" cover:`, error.message);
          // Remove from migrated set so it can be retried
          migratedIdsRef.current.delete(tournament.id);
        }
      }

      setIsMigrating(false);
      console.log("Cover migration complete!");
    };

    if (tournaments.length > 0 && !loading) {
      migrateBase64Covers();
    }
  }, [tournaments, loading]);

  return (
    <div className="tournament-page">
      <PageHeader
        title="Tournaments"
        count={totalItems}
        actions={
          <>
            <Segmented
              ariaLabel="View"
              className="view-toggle"
              value={viewMode}
              onChange={(mode) => handleViewModeChange(mode)}
              options={[
                {
                  value: "table",
                  icon: <Table size={16} />,
                  label: <span className="ui-visually-hidden">Table View</span>,
                },
                {
                  value: "calendar",
                  icon: <Calendar size={16} />,
                  label: <span className="ui-visually-hidden">Calendar View</span>,
                },
              ]}
            />
            <FilterButton count={filtersApplied} onClick={() => setIsFilterDialogOpen(true)} />
            {canManageTournaments(currentUser) && (
              <div className="create-button-group">
                <Button
                  className="create-button"
                  icon={<Plus size={18} />}
                  onClick={() => (window.location.href = `/dashboard/add-tournament`)}
                >
                  Create new tournament
                </Button>
                <Menu
                  label="More options"
                  items={[
                    {
                      label: "Create from existing",
                      icon: <Plus size={16} />,
                      onClick: () => setShowCreateFromExistingDialog(true),
                    },
                    {
                      label: "Quick Tournament",
                      icon: <Plus size={16} />,
                      onClick: () => setShowFastTournamentDialog(true),
                      disabled: true,
                    },
                  ]}
                  trigger={({ ref, ...triggerProps }) => (
                    <Button {...triggerProps} ref={ref} className="create-menu-button" aria-label="More options" data-tooltip="More options">
                      <ChevronDown size={18} />
                    </Button>
                  )}
                />
              </div>
            )}
          </>
        }
      />

      <FilterDialog
        isOpen={isFilterDialogOpen}
        onApply={handleFilterApply}
        onClose={() => setIsFilterDialogOpen(false)}
        filters={filters}
      />

      <FastTournamentDialog isOpen={showFastTournamentDialog} onClose={() => setShowFastTournamentDialog(false)} />
      <CreateFromExistingDialog
        isOpen={showCreateFromExistingDialog}
        onClose={() => setShowCreateFromExistingDialog(false)}
      />
      <DeleteTournamentDialog
        isOpen={showDeleteDialog}
        tournamentName={tournamentToDelete?.name || ""}
        onDelete={handleDeleteConfirm}
        onClose={(update) => {
          setShowDeleteDialog(false);
          setTournamentToDelete(null);
        }}
      />

      {/* Quick Filters */}
      <Toolbar
        className="quick-filters"
        actions={
          hasActiveFilters && (
            <Button variant="ghost" size="sm" className="reset-filters-btn" icon={<X size={16} />} onClick={handleResetFilters} aria-label="Reset filters">
              Reset Filters
            </Button>
          )
        }
      >
        <Select
          size="sm"
          className="quick-filter-select"
          value={currentFilters.status}
          onChange={(e) => handleQuickFilterChange("status", e.target.value)}
          aria-label="Filter by status"
        >
          <option value="All">All Status</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="open">Registration Open</option>
          <option value="closed">Registration Closed</option>
          <option value="not-open">Registration Not Open</option>
        </Select>

        <Select
          size="sm"
          className="quick-filter-select"
          value={currentFilters.location}
          onChange={(e) => handleQuickFilterChange("location", e.target.value)}
          aria-label="Filter by location"
          disabled={locations.length === 0}
        >
          <option value="All">All Locations</option>
          {locations.map((loc) => (
            <option key={loc.id} value={loc.id}>
              {loc.name}
            </option>
          ))}
        </Select>
      </Toolbar>

      {viewMode === "table" ? (
        <div className="tournaments-table-wrapper">
          <DataTable
            id={"tournaments-table"}
            data={tournaments}
            editable
            includeGrid
            label={"tournaments"}
            pageNumber={pageNumber}
            pageSize={pageSize}
            totalPages={totalPages}
            totalItems={totalItems}
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            onViewItem={handleViewTournament}
            onPageChange={handlePageChange}
            onSortChange={handleSortChange}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            canDelete={canManageTournaments(currentUser)}
            onDelete={handleDeleteClick}
            loading={loading}
            columns={[
              { key: "name", label: "Name", render: (_: any, row: ITournamentItem) => row.name, sortable: true },
              { key: "type", label: "Type", render: (_: any, row: ITournamentItem) => row.type },
              {
                key: "location",
                label: "Location",
                render: (_: any, row: ITournamentItem) => row.location?.name || "-",
                sortable: true,
              },
              {
                key: "startDate",
                label: "Date",
                sortable: true,
                render: (_: any, row: ITournamentItem) =>
                  `${moment(row.startDate).format("DD MMM YYYY")} - ${moment(row.endDate).format("DD MMM YYYY")}`,
              },
              {
                key: "categories",
                label: "# of Categories",
                render: (_: any, row: ITournamentItem) => row.categories.length,
              },
              {
                key: "status",
                label: "Status",
                render: (_: any, row: ITournamentItem) => {
                  // Use status from API if available, otherwise fall back to computed status
                  let status =
                    row.status ||
                    (() => {
                      if (row.isActive) return "Active";
                      if (row.registrationOpen) return "Registration Open";
                      if (row.registrationClosed) return "Registration Closed";
                      return "Registration Not Open";
                    })();

                  // Capitalize "Completed" if it comes from API
                  if (status.toLowerCase() === "completed") {
                    status = "Completed";
                  }

                  // For "upcoming" status, show registration status instead
                  if (status.toLowerCase() === "upcoming") {
                    if (row.registrationOpen) {
                      status = "Registration Open";
                    } else if (row.registrationClosed) {
                      status = "Registration Closed";
                    } else {
                      status = "Registration Not Open";
                    }
                  }

                  const getStatusTone = (): TagTone => {
                    const statusLower = status.toLowerCase();
                    // Check for "not open" first before "open" to avoid false matches
                    if (statusLower.includes("not open")) return "warn";
                    if (statusLower.includes("completed")) return "neutral";
                    if (statusLower.includes("active")) return "ok";
                    if (statusLower.includes("open")) return "info";
                    if (statusLower.includes("closed")) return "danger";
                    return "warn";
                  };

                  return <Tag tone={getStatusTone()}>{status}</Tag>;
                },
              },
              {
                key: "isPublished",
                label: "Publish Status",
                render: (_: any, row: ITournamentItem) => {
                  return <Tag tone={row.isPublished ? "ok" : "neutral"}>{row.isPublished ? "Published" : "Not Published"}</Tag>;
                },
              },
            ]}
          />
          {showInitialLoader && loading && (
            <div className="tournaments-loader-overlay">
              <Spinner size="lg" />
            </div>
          )}
        </div>
      ) : (
        <div className="tournament-calendar-wrapper">
          <TournamentCalendar
            loading={loading}
            tournaments={tournaments}
            currentMonth={calendarMonth}
            onMonthChange={handleCalendarMonthChange}
            onViewTournament={handleViewTournament}
          />
        </div>
      )}
    </div>
  );
};

export default Tournament;
